<template>
  <main class="event-page">
    <div v-if="isLoading" class="event-state">Carregando evento...</div>
    <div v-else-if="!event" class="event-state event-state--empty">
      <i class="ph ph-calendar-x"></i>
      <h1>Nenhum evento disponível</h1>
      <p>{{ loadError || "Ainda não há um evento ativo. Volte em breve para conferir as novidades." }}</p>
    </div>

    <template v-else>
      <section class="event-hero">
        <div class="event-shell">
          <nav class="event-nav">
            <strong>{{ formatEventDate(event.starts_at, event.ends_at) }}</strong>
            <button
              type="button"
              class="event-primary-action"
              :disabled="!event.registrations_are_open"
              @click="openRegistrationModal"
            >
              {{ event.registrations_are_open ? "Inscrever-se" : "Inscrições encerradas" }}
            </button>
          </nav>

          <div class="event-image-wrap">
            <img :src="eventImage" :alt="event.title" class="event-image" />
          </div>

          <div class="event-copy">
            <span v-if="event.eyebrow" class="event-eyebrow">{{ event.eyebrow }}</span>
            <h1>{{ event.title }}</h1>
            <p>{{ event.hero_text }}</p>

            <div class="event-highlights">
              <div>
                <strong>{{ formatEventDate(event.starts_at, event.ends_at) }}</strong>
                <span>Data do evento</span>
              </div>
              <div>
                <strong>{{ event.is_free ? "Gratuito" : formatCurrency(event.price) }}</strong>
                <span>Investimento</span>
              </div>
              <div class="event-highlight-wide">
                <strong>{{ event.address }}</strong>
                <span>Local do evento</span>
              </div>
              <div>
                <strong>{{ event.registrations_are_open ? "Inscrições abertas" : "Inscrições encerradas" }}</strong>
                <span>{{ registrationDeadlineLabel }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="event-content">
        <div class="event-shell event-content-grid">
          <div>
            <span v-if="event.content_eyebrow" class="event-eyebrow">{{ event.content_eyebrow }}</span>
            <h2>{{ event.content_title }}</h2>
            <p>{{ event.content_text }}</p>
            <div v-if="event.benefits?.length" class="event-benefits">
              <div v-for="(benefit, index) in event.benefits" :key="benefit">
                <i :class="benefitIcons[index] || 'ph ph-check-circle'"></i>
                <span>{{ benefit }}</span>
              </div>
            </div>
          </div>

          <aside class="event-cta-card">
            <span class="event-eyebrow">{{ event.title }}</span>
            <h2>{{ event.registrations_are_open ? "Garanta sua participação" : "Inscrições encerradas" }}</h2>
            <p v-if="event.registrations_are_open">
              Preencha seus dados em poucos passos. Você não sairá desta página.
            </p>
            <p v-else>Este evento continua disponível para consulta, mas não recebe novas inscrições.</p>
            <button
              type="button"
              class="event-primary-action event-primary-action--wide"
              :disabled="!event.registrations_are_open"
              @click="openRegistrationModal"
            >
              {{ event.registrations_are_open ? "Fazer inscrição" : "Período encerrado" }}
            </button>
          </aside>
        </div>
      </section>
    </template>

    <div
      v-if="modalOpen && event"
      class="event-modal-backdrop"
      role="presentation"
      @click.self="closeRegistrationModal"
    >
      <section class="event-modal" role="dialog" aria-modal="true" aria-labelledby="event-modal-title">
        <button ref="closeButton" type="button" class="event-modal-close" aria-label="Fechar" @click="closeRegistrationModal">
          <i class="ph ph-x"></i>
        </button>

        <div class="event-modal-progress" aria-label="Progresso da inscrição">
          <span :class="{ active: modalStep === 'form' }">1. Informações pessoais</span>
          <span v-if="!event.is_free" :class="{ active: modalStep === 'payment' }">2. Pagamento</span>
        </div>

        <form v-if="modalStep === 'form'" @submit.prevent="submitRegistration">
          <header class="event-modal-header">
            <span class="event-eyebrow">Inscrição</span>
            <h2 id="event-modal-title">{{ event.title }}</h2>
            <p>Conte um pouco sobre você para garantir sua participação.</p>
          </header>

          <div v-if="formError" class="event-alert event-alert--error" role="alert">{{ formError }}</div>

          <div class="event-form-grid">
            <label class="event-field event-field--wide">
              Nome completo
              <input v-model.trim="form.name" required maxlength="255" type="text" autocomplete="name" />
            </label>
            <label class="event-field">
              E-mail
              <input v-model.trim="form.email" required maxlength="255" type="email" autocomplete="email" />
            </label>
            <label class="event-field">
              Celular (WhatsApp)
              <input v-model.trim="form.whatsapp" required maxlength="15" type="tel" inputmode="numeric" autocomplete="tel" @input="formatWhatsapp" />
            </label>
            <label class="event-field" :class="{ 'event-field--wide': event.show_videira_relationship === false }">
              Qual célula você pertence atualmente?
              <select v-model="form.cell_name" required>
                <option value="" disabled>Selecione uma opção</option>
                <option v-for="cell in cellOptions" :key="cell" :value="cell">{{ cell }}</option>
              </select>
            </label>
            <label v-if="event.show_videira_relationship !== false" class="event-field">
              Você conhece a Videira?
              <select v-model="form.videira_relationship" required>
                <option value="" disabled>Selecione uma opção</option>
                <option v-for="option in relationshipOptions" :key="option" :value="option">{{ option }}</option>
              </select>
            </label>
            <label class="event-field event-field--wide">
              Qual é a sua expectativa para esse evento?
              <textarea v-model.trim="form.expectation" required maxlength="2000" rows="4"></textarea>
            </label>
            <label v-if="!event.is_free && event.allows_payment_on_event_day" class="event-field event-field--wide">
              Quando você deseja realizar o pagamento?
              <select v-model="form.payment_timing" required>
                <option value="" disabled>Selecione uma opção</option>
                <option value="now">Pagar agora via Pix</option>
                <option value="event_day">Pagar no dia do evento</option>
              </select>
            </label>
          </div>

          <button class="event-primary-action event-primary-action--wide" type="submit" :disabled="isSubmitting">
            {{ registrationActionLabel }}
          </button>
          <button v-if="!event.is_free" class="event-link-action" type="button" @click="modalStep = 'payment'">
            Já me inscrevi / ver dados do Pix
          </button>
        </form>

        <div v-else-if="modalStep === 'payment'" class="event-payment-step">
          <header class="event-modal-header">
            <span class="event-eyebrow">Pagamento via Pix</span>
            <h2 id="event-modal-title">{{ registrationCreated ? "Inscrição realizada!" : "Dados para pagamento" }}</h2>
            <p>{{ event.payment_instructions || "Faça o Pix e envie o comprovante pelo WhatsApp." }}</p>
          </header>
          <div class="event-pix-card">
            <span>Valor</span>
            <strong class="event-pix-price">{{ formatCurrency(event.price) }}</strong>
            <span>Chave Pix — {{ event.pix_key_type }}</span>
            <strong>{{ event.pix_key }}</strong>
            <button type="button" class="event-copy-action" @click="copyPixKey">
              <i class="ph ph-copy"></i> {{ pixCopied ? "Copiado!" : "Copiar chave Pix" }}
            </button>
          </div>
          <a :href="whatsappLink" target="_blank" rel="noopener" class="event-whatsapp-action">
            <i class="ph ph-whatsapp-logo"></i> Enviar comprovante
          </a>
          <button v-if="!registrationCreated" type="button" class="event-link-action" @click="modalStep = 'form'">Voltar para a inscrição</button>
        </div>

        <div v-else class="event-success-step">
          <i class="ph ph-check-circle"></i>
          <h2 id="event-modal-title">Inscrição realizada!</h2>
          <p>{{ registrationSuccessMessage || `Sua participação em ${event.title} foi registrada com sucesso.` }}</p>
          <button type="button" class="event-primary-action event-primary-action--wide" @click="closeRegistrationModal">Concluir</button>
        </div>
      </section>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import api from "../../config/axios.js";
import legacyEventImage from "../../assets/images/events/conexao-empresarial.png";
import { formatEventDateRange } from "../../utils/eventDate";

type EventData = {
  id: number;
  title: string;
  slug: string;
  eyebrow: string | null;
  hero_text: string;
  starts_at: string;
  ends_at: string | null;
  address: string;
  registration_ends_at: string;
  content_eyebrow: string | null;
  content_title: string;
  content_text: string;
  benefits: string[];
  background_image_url: string | null;
  is_free: boolean;
  allows_payment_on_event_day: boolean;
  price: string | number;
  pix_key_type: string | null;
  pix_key: string | null;
  receipt_whatsapp: string | null;
  payment_instructions: string | null;
  registrations_are_open: boolean;
  show_videira_relationship: boolean;
};

const emptyForm = () => ({
  name: "",
  email: "",
  whatsapp: "",
  cell_name: "",
  videira_relationship: "",
  expectation: "",
  payment_timing: "",
});

export default defineComponent({
  name: "EventRegistrationPage",
  data() {
    return {
      event: null as EventData | null,
      isLoading: true,
      loadError: "",
      modalOpen: false,
      modalStep: "form" as "form" | "payment" | "success",
      form: emptyForm(),
      formError: "",
      isSubmitting: false,
      registrationCreated: false,
      registrationSuccessMessage: "",
      pixCopied: false,
      benefitIcons: ["ph ph-lightbulb", "ph ph-users-three", "ph ph-chart-line-up"],
      cellOptions: [
        "Não pertenço a nenhuma Célula", "Alfa", "Apocalipse", "Atalaia", "Atos 29", "Confis",
        "DNA", "Estratégia", "IDE", "Luz", "Luz do Mundo", "Maranata", "Peniel",
        "Ramos Frutiferos", "Salem", "Yeshua", "Emaús", "Semeadores", "Kairos", "UNO",
      ],
      relationshipOptions: [
        "Sim, sou membro ativo",
        "Sim, já participei de eventos",
        "Não, este é meu primeiro contato",
        "Ouvi falar, mas não conheço detalhes",
      ],
    };
  },
  computed: {
    eventImage(): string {
      return this.event?.background_image_url || legacyEventImage;
    },
    registrationDeadlineLabel(): string {
      if (!this.event) return "";
      return this.event.registrations_are_open
        ? `Até ${this.formatDateTime(this.event.registration_ends_at)}`
        : "O período de inscrições terminou";
    },
    whatsappLink(): string {
      const phone = String(this.event?.receipt_whatsapp || "").replace(/\D/g, "");
      const message = encodeURIComponent(`Olá! Fiz minha inscrição no evento ${this.event?.title || ""} e quero enviar o comprovante do Pix.`);
      return phone ? `https://wa.me/55${phone}?text=${message}` : "#";
    },
    registrationActionLabel(): string {
      if (this.isSubmitting) return "Enviando...";
      if (this.event?.is_free || this.form.payment_timing === "event_day") return "Finalizar inscrição";
      return "Avançar para o Pix";
    },
  },
  watch: {
    "$route.params.slug"() {
      this.fetchEvent();
    },
  },
  mounted() {
    this.fetchEvent();
    window.addEventListener("keydown", this.handleEscape);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleEscape);
    document.body.style.overflow = "";
  },
  methods: {
    async fetchEvent() {
      this.isLoading = true;
      this.loadError = "";
      this.event = null;
      const slug = this.$route.params.slug as string | undefined;
      try {
        const response = await api.axios.get(slug ? `/api/events/${slug}` : "/api/events/current");
        this.event = response.data.event;
        document.title = `${this.event?.title || "Evento"} | Sanvault`;
        if (!slug && this.event) {
          await this.$router.replace(`/eventos/${this.event.slug}`);
        }
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } }).response?.status;
        this.loadError = status === 404 ? "Nenhum evento foi encontrado." : "Não foi possível carregar o evento agora.";
      } finally {
        this.isLoading = false;
      }
    },
    openRegistrationModal() {
      if (!this.event?.registrations_are_open) return;
      this.modalOpen = true;
      this.modalStep = "form";
      this.formError = "";
      this.registrationCreated = false;
      this.registrationSuccessMessage = "";
      document.body.style.overflow = "hidden";
      this.$nextTick(() => (this.$refs.closeButton as HTMLButtonElement | undefined)?.focus());
    },
    closeRegistrationModal() {
      this.modalOpen = false;
      document.body.style.overflow = "";
    },
    handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && this.modalOpen) this.closeRegistrationModal();
    },
    async submitRegistration() {
      if (!this.event) return;
      this.isSubmitting = true;
      this.formError = "";
      try {
        const response = await api.axios.post(`/api/events/${this.event.slug}/registrations`, this.form);
        this.registrationCreated = true;
        const paymentTiming = response.data.payment_timing as string | null;
        this.registrationSuccessMessage = paymentTiming === "event_day"
          ? `Sua inscrição em ${this.event.title} foi confirmada. O pagamento será realizado no dia do evento.`
          : `Sua participação em ${this.event.title} foi registrada com sucesso.`;
        this.form = emptyForm();
        this.modalStep = response.data.requires_payment ? "payment" : "success";
      } catch (error: unknown) {
        const response = (error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }).response;
        const firstValidationError = response?.data?.errors
          ? Object.values(response.data.errors)[0]?.[0]
          : null;
        this.formError = firstValidationError || response?.data?.message || "Não foi possível finalizar sua inscrição.";
      } finally {
        this.isSubmitting = false;
      }
    },
    formatWhatsapp() {
      const digits = this.form.whatsapp.replace(/\D/g, "").slice(0, 11);
      if (digits.length <= 2) this.form.whatsapp = digits;
      else if (digits.length <= 6) this.form.whatsapp = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
      else if (digits.length <= 10) this.form.whatsapp = `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
      else this.form.whatsapp = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    },
    async copyPixKey() {
      if (!this.event?.pix_key) return;
      try {
        await navigator.clipboard.writeText(this.event.pix_key);
      } catch {
        const textArea = document.createElement("textarea");
        textArea.value = this.event.pix_key;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        textArea.remove();
      }
      this.pixCopied = true;
      window.setTimeout(() => (this.pixCopied = false), 2200);
    },
    formatCurrency(value: string | number) {
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value || 0));
    },
    formatEventDate(startsAt: string, endsAt?: string | null) {
      return formatEventDateRange(startsAt, endsAt);
    },
    formatDateTime(value: string) {
      return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
    },
  },
});
</script>

<style scoped>
.event-page { min-height: 100vh; background: var(--sv-surface); color: var(--sv-text); }
.event-shell { max-width: 1180px; margin: 0 auto; padding: 0 24px; }
.event-state { min-height: 100vh; display: grid; place-content: center; text-align: center; padding: 32px; }
.event-state i { color: var(--sv-blue-600); font-size: 4rem; }
.event-state h1 { margin: 18px 0 8px; }
.event-state p { color: var(--sv-muted); }
.event-hero { padding-bottom: 76px; }
.event-nav { height: 76px; display: flex; align-items: center; justify-content: space-between; color: #fff; background: var(--sv-navy-950); margin: 0 calc(50% - 50vw); padding: 0 max(24px, calc((100vw - 1132px) / 2)); }
.event-image-wrap { margin: 0 calc(50% - 50vw) 54px; width: 100vw; overflow: hidden; background: #02060d; }
.event-image { display: block; width: 100%; height: clamp(230px, 36vw, 460px); object-fit: cover; }
.event-copy { max-width: 850px; }
.event-copy h1 { font-size: clamp(2.4rem, 6vw, 4.8rem); line-height: 1; margin: 16px 0 22px; }
.event-copy > p, .event-content p { color: #34455c; font-size: 1.08rem; line-height: 1.75; }
.event-eyebrow { color: var(--sv-blue-600); font-size: .78rem; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
.event-highlights { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; margin-top: 34px; }
.event-highlights div, .event-benefits div { background: #fff; border: 1px solid var(--sv-border); border-radius: 12px; padding: 18px; }
.event-highlights strong, .event-highlights span { display: block; }
.event-highlights span { color: var(--sv-muted); margin-top: 4px; }
.event-highlight-wide { grid-column: 1 / -1; }
.event-content { padding: 76px 0; background: #fff; }
.event-content-grid { display: grid; grid-template-columns: minmax(0, 1.3fr) minmax(320px, .7fr); gap: 50px; align-items: start; }
.event-content h2, .event-modal h2 { font-size: clamp(1.9rem, 4vw, 3rem); line-height: 1.08; margin: 12px 0 18px; }
.event-benefits { display: grid; gap: 12px; margin-top: 26px; }
.event-benefits div { display: flex; gap: 14px; align-items: center; }
.event-benefits i { color: var(--sv-blue-600); font-size: 1.5rem; }
.event-cta-card { border: 1px solid var(--sv-border); border-radius: 16px; box-shadow: 0 22px 60px rgba(23,32,38,.12); padding: 30px; }
.event-primary-action { border: 0; border-radius: 10px; background: var(--sv-blue-600); color: #fff; font-weight: 800; min-height: 46px; padding: 12px 20px; }
.event-primary-action:disabled { cursor: not-allowed; opacity: .55; }
.event-primary-action--wide { width: 100%; }
.event-modal-backdrop { position: fixed; inset: 0; z-index: 2000; display: grid; place-items: center; background: rgba(3,11,20,.74); padding: 20px; overflow-y: auto; }
.event-modal { position: relative; width: min(760px, 100%); max-height: calc(100vh - 40px); overflow-y: auto; border-radius: 18px; background: #fff; box-shadow: 0 30px 100px rgba(0,0,0,.35); padding: 32px; }
.event-modal-close { position: absolute; right: 18px; top: 18px; border: 0; border-radius: 50%; width: 42px; height: 42px; background: var(--sv-surface-soft); font-size: 1.25rem; }
.event-modal-progress { display: flex; gap: 12px; padding-right: 48px; margin-bottom: 28px; }
.event-modal-progress span { border-radius: 999px; background: var(--sv-surface-soft); color: var(--sv-muted); font-size: .8rem; font-weight: 800; padding: 8px 12px; }
.event-modal-progress span.active { background: var(--sv-blue-600); color: #fff; }
.event-modal-header p { color: var(--sv-muted); }
.event-form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; margin: 26px 0; }
.event-field { display: grid; gap: 8px; color: #34455c; font-weight: 800; }
.event-field--wide { grid-column: 1 / -1; }
.event-field input, .event-field select, .event-field textarea { width: 100%; border: 1px solid var(--sv-border); border-radius: 10px; color: var(--sv-text); font: inherit; padding: 12px 13px; }
.event-field input:focus, .event-field select:focus, .event-field textarea:focus { outline: 0; border-color: var(--sv-blue-600); box-shadow: 0 0 0 4px rgba(29,111,242,.12); }
.event-alert { border-radius: 10px; padding: 13px; font-weight: 800; margin-top: 18px; }
.event-alert--error { background: #fff0ee; color: #a33728; }
.event-link-action { display: block; width: 100%; border: 0; background: transparent; color: var(--sv-blue-600); font-weight: 800; margin-top: 14px; }
.event-pix-card { display: grid; gap: 8px; text-align: center; color: #fff; background: var(--sv-navy-950); border: 1px solid var(--sv-cyan-500); border-radius: 14px; padding: 26px; margin: 24px 0 16px; }
.event-pix-card span { color: var(--sv-cyan-500); font-weight: 800; }
.event-pix-card strong { font-size: 1.3rem; word-break: break-word; }
.event-pix-price { font-size: 2.5rem !important; margin-bottom: 14px; }
.event-copy-action, .event-whatsapp-action { display: flex; align-items: center; justify-content: center; gap: 8px; border: 0; border-radius: 10px; font-weight: 800; min-height: 46px; text-decoration: none; }
.event-copy-action { background: var(--sv-blue-600); color: #fff; margin-top: 10px; }
.event-whatsapp-action { background: #65d187; color: #102016; }
.event-success-step { text-align: center; padding: 36px 0 10px; }
.event-success-step > i { color: #198754; font-size: 4rem; }
@media (max-width: 800px) { .event-content-grid { grid-template-columns: 1fr; } }
@media (max-width: 560px) { .event-shell { padding: 0 16px; } .event-nav { padding: 0 16px; } .event-highlights, .event-form-grid { grid-template-columns: 1fr; } .event-highlight-wide, .event-field--wide { grid-column: auto; } .event-modal { padding: 24px 18px; } .event-modal-progress { flex-direction: column; } }
</style>
